import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import s from "./LazyVideo.module.scss";
import { createVideoSchema } from "@/lib/constants";

interface LazyVideoProps {
    alt?: string;
    src: string;
    defaultInView?: boolean;
    autoplay?: boolean;
    controls?: boolean;
}

export default function LazyVideo({
    alt = "",
    src = "",
    defaultInView = false,
    autoplay,
    controls,
}: LazyVideoProps) {
    const [isPlaying, setIsPlaying] = useState(autoplay); // Track if the video is playing
    const [hasBeenInView, setHasBeenInView] = useState(false);
    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: false,
        initialInView: defaultInView,
    });

    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        // Si el video está en el área visible y no ha sido reproducido aún, y el autoplay está activado
        if (inView && !hasBeenInView && autoplay) {
            videoRef.current?.play();
            setHasBeenInView(true);
            setIsPlaying(true);
        } else if (inView && !hasBeenInView && !autoplay) {
            setHasBeenInView(true)
        } else if (!inView) {
            videoRef.current?.pause();
            setIsPlaying(false);
        } else if (inView && autoplay) {
            videoRef.current?.play();
            setIsPlaying(true);
        }
    }, [inView, autoplay, hasBeenInView]);

    const handleVideoClick = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
                setIsPlaying(true);
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    };

    const videoSchema = createVideoSchema({ src, alt });

    return (
        <div className={s.lazyVideo}>
            {!isPlaying && !controls && ( // Render logo only when video is not playing
                <div className={s.logo}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="146" height="146" viewBox="0 0 146 146" fill="none">
                        <path d="M101 73.5L59.75 97.3157L59.75 49.6843L101 73.5Z" fill="#9e9a9a" />
                        <circle cx="73" cy="73" r="72.5" stroke="#9e9a9a" />
                    </svg>
                </div>
            )}
            <div
                ref={ref}
                style={{ width: "100%" }}
                className={s.contentVideo}
                onClick={handleVideoClick}
            >
                {controls ? (
                    <video
                        ref={videoRef}
                        src={hasBeenInView ? src : ''}
                        autoPlay={autoplay}
                        muted
                        controls
                        loop
                        playsInline
                    />
                ) : (
                    <video
                        ref={videoRef}
                        src={hasBeenInView ? src : ''}
                        autoPlay={autoplay}
                        muted
                        loop
                        playsInline
                    />
                )}
            </div>
            <script type="application/ld+json" async dangerouslySetInnerHTML={{ __html: JSON.stringify(videoSchema) }} />
        </div>
    );
}